import { ReactNode } from "react";
import { Navigate, useLocation, useParams } from "react-router";
import { SkyLoader } from "@skypointcloud/skypoint-ui";
import { useGetMyTicketAccessQuery } from "@/store/api/portalsApi";
import NoAccessPage from "@/views/NoAccessPage";
import { ROUTES } from "@/components/shared/constant";

interface TicketAccessGuardProps {
  mode: "agent" | "customer";
  children: ReactNode;
}

/**
 * Resolves the user's access to a specific ticket before rendering the detail
 * view. Honors the URL the user opened whenever they have access to it — only
 * redirects to the other view when they lack access to the current one. If
 * they have neither, render the shared NoAccessPage.
 */
export default function TicketAccessGuard({ mode, children }: TicketAccessGuardProps) {
  const { portalKey, itemKey, instanceid } = useParams<{
    portalKey: string;
    itemKey: string;
    instanceid?: string;
  }>();
  const location = useLocation();

  const skip = !portalKey || !itemKey;
  const { data, isLoading, isFetching, isError } = useGetMyTicketAccessQuery(
    { portalKey: portalKey ?? "", itemKey: itemKey ?? "" },
    { skip },
  );

  if (skip) return <>{children}</>;

  if (isLoading || isFetching) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <SkyLoader />
      </div>
    );
  }

  // Treat a network/server failure as "let the page render and surface its
  // own error" rather than locking the user out of a ticket they may have
  // access to.
  if (isError || !data) {
    return <>{children}</>;
  }

  if (data.isDeleted) {
    return <NoAccessPage deleted />;
  }

  if (!data.exists) {
    return <NoAccessPage />;
  }

  const tail = instanceid ? `/${instanceid}` : "";
  const qs = location.search;

  if (mode === "agent") {
    if (data.canViewAsAgent) return <>{children}</>;
    if (data.canViewAsCustomer) {
      const target =
        ROUTES.CUSTOMER_REQUEST_DETAIL.replace(":portalKey", portalKey!).replace(
          ":itemKey",
          itemKey!,
        ) +
        tail +
        qs;
      return (
        <Navigate
          to={target}
          replace
        />
      );
    }
    return <NoAccessPage />;
  }

  // mode === "customer"
  if (data.canViewAsCustomer) return <>{children}</>;
  if (data.canViewAsAgent) {
    const target =
      ROUTES.PORTAL_ITEM_DETAIL.replace(":portalKey", portalKey!).replace(":itemKey", itemKey!) +
      tail +
      qs;
    return (
      <Navigate
        to={target}
        replace
      />
    );
  }
  return <NoAccessPage />;
}
